import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { fetchPriceQuote } from '../api/pricing';
import {
  clearQuote,
  selectCartItems,
  selectCustomer,
  selectLoyaltyRedemption,
  setQuote,
} from '../store/cartSlice';
import { usePosProfile } from './PosProfileContext';

/**
 * Debounced server-authoritative cart quote.
 *
 * The hook writes quote data into Redux so Cart/Checkout can render the same
 * authoritative totals without each component issuing its own query.
 */
export default function usePriceQuote() {
  const dispatch = useDispatch();
  const items = useSelector(selectCartItems);
  const customer = useSelector(selectCustomer);
  const loyaltyRedemption = useSelector(selectLoyaltyRedemption);
  const { profile, profileData } = usePosProfile();
  const [debouncedPayload, setDebouncedPayload] = useState(null);

  const payload = useMemo(() => {
    if (!profile?.name || items.length === 0) return null;

    return {
      pos_profile: profile.name,
      company: profile.company,
      customer: customer?.name || profileData?.customer || profile.customer,
      items: items.map((item) => ({
        item_code: item.item_code,
        item_name: item.item_name,
        qty: item.qty,
        rate: item.rate,
        price_list_rate: item.rate,
        discount_percentage: item.discount_percentage || 0,
        uom: item.uom || item.stock_uom || 'Nos',
      })),
      redeem_loyalty_points: loyaltyRedemption?.enabled ? 1 : 0,
      loyalty_points: loyaltyRedemption?.points || 0,
      loyalty_program: loyaltyRedemption?.loyalty_program || customer?.loyalty_program,
    };
  }, [customer, items, loyaltyRedemption, profile, profileData]);

  useEffect(() => {
    if (!payload) {
      setDebouncedPayload(null);
      dispatch(clearQuote());
      return undefined;
    }

    const timer = setTimeout(() => setDebouncedPayload(payload), 300);
    return () => clearTimeout(timer);
  }, [dispatch, payload]);

  const query = useQuery({
    queryKey: ['priceQuote', debouncedPayload],
    queryFn: () => fetchPriceQuote(debouncedPayload),
    enabled: !!debouncedPayload,
    staleTime: 0,
    retry: 1,
    onSuccess: (quote) => {
      dispatch(setQuote(quote));
    },
    onError: () => {
      dispatch(clearQuote());
    },
  });

  return query;
}
